import React, { useRef, useEffect, useCallback, useState } from "react";
import DOMPurify from "dompurify";
import {
    Box,
    HStack,
    Icon,
    IconButton,
    Tooltip,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    Input,
    FormControl,
    FormLabel,
} from "@chakra-ui/react";
import {
    FaBold,
    FaItalic,
    FaHeading,
    FaListUl,
    FaListOl,
    FaLink,
} from "react-icons/fa6";

// Keep inline-editor + read-only renderer in one file so descriptions
// always agree on styling and sanitizer rules.

const ALLOWED_TAGS = [
    "b",
    "strong",
    "i",
    "em",
    "u",
    "a",
    "h3",
    "h4",
    "ul",
    "ol",
    "li",
    "p",
    "br",
    "div",
    "span",
];
const ALLOWED_ATTR = ["href", "target", "rel"];

// Shared styling so what the host types in the editor looks identical
// to what the guest sees on the public event page.
const contentStyles = {
    fontSize: "sm",
    lineHeight: "1.7",
    color: "gray.700",
    "& a": {
        color: "#508cf7",
        textDecoration: "underline",
    },
    "& ul, & ol": {
        paddingLeft: "1.25rem",
        marginBottom: "0.5rem",
    },
    "& li": { marginBottom: "0.25rem" },
    "& h3, & h4": {
        fontSize: "md",
        fontWeight: 700,
        color: "gray.800",
        marginTop: "0.75rem",
        marginBottom: "0.25rem",
    },
    "& p": { marginBottom: "0.5rem" },
    "& strong, & b": { fontWeight: 700, color: "gray.800" },
    "& em, & i": { fontStyle: "italic" },
};

// Small helper: make sanitized HTML safe to inject AND force any links
// to open in a new tab. We append target/rel attributes via a hook so
// DOMPurify strips anything else we didn't ask for.
const sanitize = (html) => {
    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
        if (node.tagName === "A") {
            node.setAttribute("target", "_blank");
            node.setAttribute("rel", "noopener noreferrer");
        }
    });
    const clean = DOMPurify.sanitize(html || "", {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
    });
    DOMPurify.removeAllHooks();
    return clean;
};

// Quick utility for list/card previews: turn the stored HTML into
// plain text without tags, collapsing whitespace to a single line so
// `noOfLines` truncation works cleanly. Paragraph/list breaks become
// spaces, <br> becomes a space. Keeps `<` and `>` in the original text
// by decoding any HTML entities the browser normalized into attributes.
export const stripHtml = (html) => {
    if (!html) return "";
    const hasHTML = /<[a-z][\s\S]*>/i.test(html);
    if (!hasHTML) return html;
    // Use the DOM to decode entities and drop tags safely.
    const el = document.createElement("div");
    el.innerHTML = html;
    const text = el.textContent || el.innerText || "";
    return text.replace(/\s+/g, " ").trim();
};

// Read-only renderer used on the event page.
export const RichDescription = ({ html }) => {
    if (!html || !html.trim()) return null;
    // Plain-text (pre-editor) descriptions — preserve line breaks so
    // legacy events don't lose their newlines.
    const hasHTML = /<[a-z][\s\S]*>/i.test(html);
    const content = hasHTML ? html : html.replace(/\n/g, "<br/>");
    const clean = sanitize(content);
    return (
        <Box
            sx={contentStyles}
            dangerouslySetInnerHTML={{ __html: clean }}
        />
    );
};

// WYSIWYG editor used inside Create/Edit Event.
// value is HTML, onChange receives the latest HTML string.
export const RichTextEditor = ({
    value,
    onChange,
    placeholder = "Start typing…",
    minHeight = "160px",
}) => {
    const editorRef = useRef(null);
    const initialisedRef = useRef(false);
    // Stash the user's current selection before the link modal takes
    // focus so we can restore it before running execCommand.
    const savedRangeRef = useRef(null);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");

    // Push value into the editor on first mount and on external updates
    // (e.g. opening the modal for an existing event). Don't wipe the
    // editor while the user is actively typing into it — that would
    // kill the caret.
    useEffect(() => {
        if (!editorRef.current) return;
        const next = value || "";
        const current = editorRef.current.innerHTML;

        if (!initialisedRef.current) {
            editorRef.current.innerHTML = sanitize(
                /<[a-z][\s\S]*>/i.test(next) ? next : next.replace(/\n/g, "<br/>")
            );
            initialisedRef.current = true;
            return;
        }

        if (document.activeElement !== editorRef.current && next !== current) {
            editorRef.current.innerHTML = sanitize(next);
        }
    }, [value]);

    const emitChange = useCallback(() => {
        if (!editorRef.current || !onChange) return;
        onChange(editorRef.current.innerHTML);
    }, [onChange]);

    // document.execCommand is the most pragmatic way to do simple
    // rich-text editing without pulling in a full editor framework.
    // It's marked deprecated on MDN but is still implemented by every
    // shipping browser and is fine for short-form content like this.
    const exec = (command, arg) => {
        if (!editorRef.current) return;
        editorRef.current.focus();
        try {
            document.execCommand("styleWithCSS", false, false);
        } catch (_) {
            // Some browsers throw on unknown commands — safe to ignore.
        }
        document.execCommand(command, false, arg);
        emitChange();
    };

    const applyFormat = (type) => {
        switch (type) {
            case "bold":
                return exec("bold");
            case "italic":
                return exec("italic");
            case "heading":
                return exec("formatBlock", "h3");
            case "bullet":
                return exec("insertUnorderedList");
            case "numbered":
                return exec("insertOrderedList");
            case "link":
                return openLinkModal();
            default:
                return;
        }
    };

    // Link-modal helpers ──────────────────────────────────────────────
    const openLinkModal = () => {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            savedRangeRef.current = selection.getRangeAt(0).cloneRange();
        } else {
            savedRangeRef.current = null;
        }
        setLinkUrl("https://");
        setLinkModalOpen(true);
    };

    const closeLinkModal = () => {
        setLinkModalOpen(false);
        setLinkUrl("");
    };

    const confirmLink = () => {
        const url = linkUrl.trim();
        if (!url || url === "https://") {
            closeLinkModal();
            return;
        }
        // Restore the selection we captured before the modal opened so
        // createLink wraps the right text.
        if (savedRangeRef.current && editorRef.current) {
            editorRef.current.focus();
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(savedRangeRef.current);
        }
        exec("createLink", url);
        closeLinkModal();
    };

    const tools = [
        { id: "bold", icon: FaBold, label: "Bold" },
        { id: "italic", icon: FaItalic, label: "Italic" },
        { id: "heading", icon: FaHeading, label: "Heading" },
        { id: "bullet", icon: FaListUl, label: "Bulleted list" },
        { id: "numbered", icon: FaListOl, label: "Numbered list" },
        { id: "link", icon: FaLink, label: "Link" },
    ];

    return (
        <Box>
            <HStack spacing={1} flexWrap="wrap" mb={1.5}>
                {tools.map((tool) => (
                    <Tooltip
                        key={tool.id}
                        label={tool.label}
                        placement="top"
                        hasArrow
                        fontSize="11px"
                    >
                        <IconButton
                            type="button"
                            aria-label={tool.label}
                            icon={<Icon as={tool.icon} boxSize="11px" />}
                            size="xs"
                            variant="ghost"
                            color="gray.600"
                            borderRadius="md"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => applyFormat(tool.id)}
                            _hover={{ bg: "gray.100", color: "#508cf7" }}
                        />
                    </Tooltip>
                ))}
            </HStack>

            <Box
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                data-placeholder={placeholder}
                onInput={emitChange}
                onBlur={emitChange}
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                bg="white"
                p={3}
                minH={minHeight}
                sx={{
                    ...contentStyles,
                    outline: "none",
                    "&:empty::before": {
                        content: "attr(data-placeholder)",
                        color: "var(--chakra-colors-gray-400)",
                        pointerEvents: "none",
                    },
                    "&:focus-visible": {
                        borderColor: "#508cf7",
                        boxShadow: "0 0 0 1px #508cf7",
                    },
                }}
            />

            {/* Link dialog — replaces the browser's native prompt with
                something that actually matches the rest of the UI. */}
            <Modal
                isOpen={linkModalOpen}
                onClose={closeLinkModal}
                isCentered
                size="sm"
            >
                <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(2px)" />
                <ModalContent borderRadius="xl">
                    <ModalHeader fontSize="md" fontWeight="700">
                        Add a link
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl>
                            <FormLabel
                                fontSize="xs"
                                fontWeight="600"
                                color="gray.600"
                                mb={1}
                            >
                                URL
                            </FormLabel>
                            <Input
                                autoFocus
                                size="sm"
                                borderRadius="lg"
                                placeholder="https://example.com"
                                value={linkUrl}
                                onChange={(e) => setLinkUrl(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        confirmLink();
                                    }
                                }}
                                focusBorderColor="#508cf7"
                            />
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            size="sm"
                            variant="ghost"
                            mr={2}
                            onClick={closeLinkModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            bg="#508cf7"
                            color="white"
                            _hover={{ bg: "#226cf1" }}
                            onClick={confirmLink}
                        >
                            Add link
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default RichTextEditor;
